import { inject, Injectable } from '@angular/core';
import { RootStore } from '@sama/app-state';
import { InjectableSuperclass } from '@sama/ng-core';
import {
  catchError,
  concatMap,
  forkJoin,
  Observable,
  of,
  ReplaySubject,
  retry,
  share,
  Subject,
} from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { HttpErrorHandler } from '../../../cross-team-shared/messaging/http-error-handler';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { assertStore } from '../../../cross-team-shared/misc-utils';
import { strictValues } from '../../../cross-team-shared/misc-utils/strict-record';
import { NavigationWarningService } from '../../../shared/navigation/navigation-warning.service';

export const RETRY_DELAY_TIME = 3000;

type RequestQueue = Subject<[Observable<any>, any]>;

/*
 * This service manages HTTP requests that persist actions to the server while
 * not blocking user input. Multiple queues are available, and requests are
 * automatically retried, displaying error/success messages as appropriate.
 */
@Injectable()
export class PersistenceService extends InjectableSuperclass {
  private queues: StrictRecord<string, RequestQueue> = {};
  private store = new RootStore<
    StrictRecord<
      string,
      { pendingCount: number; retryCount: number; lastSaveTimestamp?: number }
    >
  >({});

  private httpErrorHandler = inject(HttpErrorHandler);
  private messagingService = inject(MessagingService);

  constructor(navigationWarningService: NavigationWarningService) {
    super();
    navigationWarningService.register(() => !this.areQueuesEmpty(), this);
  }

  enqueue$<T>(action$: Observable<T>, queueName: string): Observable<T> {
    let queue$ = this.getQueue$(queueName);

    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!queue$) {
      queue$ = this.createQueue(queueName);
    }
    const sharedAction$ = action$.pipe(share());
    const complete$ = new ReplaySubject<T>(1);

    this.incrementQueueCounter(queueName);

    queue$.next([sharedAction$, complete$]);

    return complete$;
  }

  getQueueCounter(queueName: string) {
    return this.getQueueStore(queueName).state().pendingCount;
  }

  isSaving$() {
    return this.store.$.pipe(
      map(() => !this.areQueuesEmpty()),
      distinctUntilChanged(),
    );
  }

  getRetryCount$() {
    return this.store.$.pipe(
      map((state) =>
        strictValues(state).reduce(
          (sum, { retryCount }) => sum + retryCount,
          0,
        ),
      ),
    );
  }

  getLastSaveTimestamp$() {
    return this.store.$.pipe(
      map((state) =>
        Math.max(
          ...strictValues(state).map(
            ({ lastSaveTimestamp }) => lastSaveTimestamp ?? 0,
          ),
        ),
      ),
      filter((timestamp) => timestamp > 0),
    );
  }

  private incrementQueueCounter(queueName: string) {
    this.getQueueStore(queueName)('pendingCount').setUsing(
      (count) => count + 1,
    );
  }

  private decrementQueueCounter(queueName: string) {
    this.getQueueStore(queueName)('pendingCount').setUsing(
      (count) => count - 1,
    );
  }

  private getQueue$(queueName: string) {
    const queue$ = this.queues[queueName] ?? this.createQueue(queueName);
    this.queues[queueName] = queue$;
    return queue$;
  }

  private setUpSubscriptionToQueue(queueName: string) {
    this.subscribeTo(
      this.getQueue$(queueName).pipe(
        concatMap(([action$, complete$]) =>
          forkJoin([
            this.handleErrorsAndRetries(action$, queueName),
            of(complete$),
          ]),
        ),
      ),
      ([result, complete$]) => {
        if (this.getQueueStore(queueName).state().retryCount > 0) {
          this.messagingService.success(
            'Connection re-established. Action saved.',
          );
        }

        this.store.batch(() => {
          this.getQueueStore(queueName).assign({
            retryCount: 0,
            lastSaveTimestamp: Date.now(),
          });
          this.decrementQueueCounter(queueName);
        });
        complete$.next(result);
        complete$.complete();
      },
    );
  }

  private createQueue(queueName: string) {
    this.store(queueName).set({ pendingCount: 0, retryCount: 0 });
    const queue$: RequestQueue = new Subject();
    this.queues[queueName] = queue$;
    this.setUpSubscriptionToQueue(queueName);
    return queue$;
  }

  private handleErrorsAndRetries(action$: Observable<any>, queueName: string) {
    return action$.pipe(
      catchError((error: unknown) => {
        const retryCountStore = this.getQueueStore(queueName)('retryCount');
        if (retryCountStore.state() === 0) {
          this.httpErrorHandler.handleError(error);
        }

        retryCountStore.setUsing((count) => count + 1);
        throw error;
      }),
      retry({ delay: RETRY_DELAY_TIME }),
    );
  }

  private areQueuesEmpty() {
    return strictValues(this.store.state()).every(
      (state) => state.pendingCount === 0,
    );
  }

  private getQueueStore(queueName: string) {
    if (!this.queues[queueName]) {
      this.createQueue(queueName);
    }
    const queueStore = this.store(queueName);
    assertStore(
      queueStore,
      'queue store should be initialized when queue is created',
    );
    return queueStore;
  }
}
